import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import "./InquiryForm.css";
import { getProductApiUrl, getProductCodeFromPath, getProductPath } from '../productUrls';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const InquiryForm = () => {
  const { pathname } = useLocation();
  const productCode = getProductCodeFromPath(pathname);
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const fetchProduct = async () => {
      try {
        const response = await fetch(
          getProductApiUrl(API_BASE_URL, productCode)
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch product: ${response.statusText}`);
        }
        const data = await response.json();
        setProduct(data);
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [productCode]);

  const showConfirmation = async () => {
    Swal.fire({
      title: 'Confirm Your Details',
      html: `
        <div style="text-align: left; margin-bottom: 20px;">
          <p>Please confirm your shipping details:</p>
          <p><strong>Delivery Location:</strong><br>${deliveryLocation}</p>
          <p><strong>Phone Number:</strong><br>${phoneNumber}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Edit Details',
      confirmButtonColor: '#0288d1',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'swal-popup',
        title: 'swal-title',
        confirmButton: 'swal-confirm-button',
        cancelButton: 'swal-cancel-button',
        htmlContainer: 'swal-html-container'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        submitInquiry();
      }
    });
  };

  const submitInquiry = async () => {
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("companyName", companyName || "");
      formData.append("phoneNumber", phoneNumber);
      formData.append("quantity", quantity);
      formData.append("deliveryLocation", deliveryLocation);
      formData.append("message", message);
      formData.append("productId", product.productID);
      
      if (attachment) {
        formData.append("attachment", attachment);
      }
      
      const response = await fetch(`${API_BASE_URL}/Inquiries/post`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Inquiry submitted successfully, we will contact you shortly.',
          icon: 'success',
          timer: 1500,
          confirmButtonText: 'OK',
          confirmButtonColor: '#0288d1',
          customClass: {
            popup: 'swal-popup',
            title: 'swal-title',
            confirmButton: 'swal-confirm-button'
          }
        }).then((result) => {
          if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) {
            navigate(getProductPath(productCode));
          }
        });
      } else {
        const errorData = await response.json().catch(() => null);
        const errorMsg = errorData ? errorData.message || 'Failed to submit inquiry.' : 'Failed to submit inquiry.';
        
        Swal.fire({
          title: 'Error!',
          text: errorMsg,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
          customClass: {
            popup: 'swal-popup',
            title: 'swal-title',
            confirmButton: 'swal-confirm-button'
          }
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'An error occurred while submitting your inquiry.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
        customClass: {
          popup: 'swal-popup',
          title: 'swal-title',
          confirmButton: 'swal-confirm-button'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValidPhoneNumber = /^\d{8,}$/.test(phoneNumber);
    if (!isValidPhoneNumber) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid phone number (at least 8 digits).',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
        customClass: {
          popup: 'swal-popup',
          title: 'swal-title',
          confirmButton: 'swal-confirm-button'
        }
      });
      return;
    }
    showConfirmation();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: 'Maximum file size is 5MB.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
        e.target.value = null;
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: 'Invalid File Type',
          text: 'Please upload an image (JPEG, PNG), PDF, or Word document.',
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
        e.target.value = null;
        return;
      }
      
      setAttachment(file);
    }
  };

  if (isLoading) {
    return (
      <div className="user-inquiry-form-container">
        <div className="user-inquiry-form-wrapper">
          <div className="user-inquiry-form-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="user-inquiry-form-container">
        <div className="user-inquiry-form-wrapper">
          <div className="user-inquiry-form-error">
            <p>Product not found.</p>
            <Link to="/category">Back to Products</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-inquiry-form-container">
      <div className="user-inquiry-form-wrapper">
        <div className="user-inquiry-form">
          <div className="user-inquiry-form-image-container">
            <h2 className="user-inquiry-form-title">
              Inquiry for {product.name}
            </h2>
            <img
              className="user-inquiry-form-image"
              src={`${product.mainImageUrl}`}
              alt={product.name}
            />
          </div>

          <form onSubmit={handleSubmit} className="user-inquiry-form-fields">
            <div>
              <label className="user-inquiry-form-label">Name:</label>
              <input
                type="text"
                className="user-inquiry-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Enter your full name"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="user-inquiry-form-label">Company Name:</label>
              <input
                type="text"
                className="user-inquiry-form-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter your company (optional)"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="user-inquiry-form-label">Phone Number:</label>
              <input
                type="tel"
                className="user-inquiry-form-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                placeholder="Enter your contact number"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="user-inquiry-form-label">Quantity:</label>
              <input
                type="number"
                className="user-inquiry-form-input"
                value={quantity}
                min="1"
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="user-inquiry-form-label">
                Delivery Location:
              </label>
              <input
                type="text"
                className="user-inquiry-form-input"
                value={deliveryLocation}
                onChange={(e) => setDeliveryLocation(e.target.value)}
                required
                placeholder="Enter your delivery address"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="user-inquiry-form-label">Attachment:</label>
              <input
                type="file"
                className="user-inquiry-form-file-input"
                accept="image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
                disabled={isSubmitting}
              />
              {attachment && (
                <p className="user-inquiry-form-file-info">
                  Selected file: {attachment.name} ({formatFileSize(attachment.size)})
                </p>
              )}
            </div>
            <div>
              <label className="user-inquiry-form-label">Message:</label>
              <textarea
                className="user-inquiry-form-textarea"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                placeholder="Please provide any additional details about your inquiry"
                disabled={isSubmitting}
              />
            </div>
            <button 
              type="submit" 
              className="user-inquiry-form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                  Submitting...
                </>
              ) : (
                "Submit Inquiry"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
};

export default InquiryForm;
